/**
 * LicenseBuilder Component
 */

import { baseline } from '../../../styles/grid';
import {
  contextualParamMapping,
  contextualParamStep,
  licenseBuildingSteps,
  programmingMedia,
  spotCountStep,
  termStep,
} from './config';
import './styles.scss';

import { find, isEmpty, last } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import * as Velocity from 'velocity-animate';

import { OnboardingContext } from '../../../../contexts/onboarding';
import { IOnboardingContext, OnboardingAchievement } from '../../../../contexts/onboarding/types';
import { getContextualParamTitle } from '../../../../redux/helpers/license';
import Button from '../../../atoms/buttons/Button';
import { IconWarningLarge, LogoBopper } from '../../../atoms/images/Icon';
import Heading from '../../../atoms/text/Heading';
import Drawer from '../../../molecules/modals/Drawer';

interface IOwnStateProps {
  editingUsageIndex: number | null;
  isLicenseValid: boolean;
  licenseComponents: GQL.ILicenseComponents | {};
  licenseUsages: ILicenseUsage[];
  termsState: ITermsState;
}

interface IContextsProps {
  onboarding: Pick<IOnboardingContext, 'unlockAchievement'>;
}

interface IOwnProps {
  className?: string;
  usages?: ILicenseUsage[];
  onClose(): void;
  onSave(usages: ILicenseUsage[]): void;
}

interface IOwnState {
  currentStep: LicenseBuildingStepNumber;
  hasDeletedAllMedia: boolean;
  drawerOpen: boolean;
  previousTermsState: ITermsState;
}

type LicenseBuilderProps =
  & IOwnProps
  & IOwnStateProps
  & IContextsProps;

export const getContextualParam = (currentMedium: LicenseMediaTypeIdentifier) =>
  (params: ILicenseContextualParamMapping[]) =>
    find(params, ({ medium, param }) => medium.indexOf(currentMedium) > -1)!;

const classes = new BEM('license-builder');

class LicenseBuilder extends React.Component<LicenseBuilderProps, IOwnState> {
  drawerElement = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      currentStep: 1,
      hasDeletedAllMedia: false,
      drawerOpen: true,
      previousTermsState: {...this.props.termsState},
    };
  }

  componentDidMount() {
    this.setCurrentDisplayState();
  }

  componentDidUpdate(prevProps: LicenseBuilderProps, prevState: IOwnState) {
    this.setCurrentDisplayState(prevProps);

    if (prevState.currentStep !== this.state.currentStep) {
      const scroller = this.drawerElement.current!.getElementsByClassName('drawer__content')[0];
      const steps = this.drawerElement.current!.getElementsByClassName('license-builder__step');
      Velocity(last(steps), 'scroll', {
        axis: 'x',
        container: scroller,
        delay: 1500,
        duration: 500,
      });
    }
  }

  onClickSave = () => {
    this.setState({ drawerOpen: false });
    this.props.onSave(this.props.licenseUsages);
  }

  onClose = (cancel: boolean) => {
    if (cancel) {
      if (this.props.licenseUsages.length === 0) {
        // We send an empty array to save
        this.props.onSave([]);
      }
    } else {
      this.props.onboarding.unlockAchievement(OnboardingAchievement.BuildLicense);
    }

    this.props.onClose();
  }

  render() {
    const {
      className,
      editingUsageIndex,
      licenseComponents,
      licenseUsages,
    } = this.props;

    const { currentStep, drawerOpen } = this.state;

    const steps: JSX.Element[] = [];
    let LicenseBuildingStep: LicenseBuildingStepComponentClass;
    let stepInfo: ILicenseBuildingStepInfo | null;

    for (let step = 1; step <= currentStep; step++) {
      if (step === contextualParamStep) {
        if (editingUsageIndex !== null) {
          const currentUsage = licenseUsages[editingUsageIndex];
          if (currentUsage) {
            const currentMediumId = currentUsage.medium.id as LicenseMediaTypeIdentifier;
            const contextualComponents = licenseBuildingSteps[step - 1] as ILicenseBuildingStepInfo[];
            const contextualParam = getContextualParam(currentMediumId)(contextualParamMapping);
            stepInfo = find(contextualComponents, { id: contextualParam.param }) as ILicenseBuildingStepInfo;
            stepInfo.title = getContextualParamTitle(currentUsage);
          } else {
            stepInfo = null;
          }
        } else {
          stepInfo = null;
        }
      } else {
        // If the selected medium is a programming medium, then skip Term/SpotCount steps
        if (editingUsageIndex !== null) {
          const currentUsage = licenseUsages[editingUsageIndex];
          if (currentUsage) {
            const currentMediumId = currentUsage.medium.id;
            if (programmingMedia.indexOf(currentMediumId) > -1 && (step === termStep || step === spotCountStep)) {
              continue;
            }
          }
        }
        stepInfo = licenseBuildingSteps[step - 1] as ILicenseBuildingStepInfo;
      }

      if (stepInfo != null) {
        LicenseBuildingStep = stepInfo.component;
        const connection = licenseComponents[stepInfo.id];

        if (connection) {
          const stepProps: ILicenseComponentProps<LicenseComponentEdge> = {
            data: connection.edges!,
            title: stepInfo.title,
          };

          steps.push(
            <LicenseBuildingStep
              key={stepInfo.id}
              {...classes('step') }
              {...stepProps}
            />,
          );
        }
      }
    }

    return (
      <>
        <Helmet>
          <title>Set Your License</title>
        </Helmet>
        <Drawer
          closeButtonLabel={this.state.hasDeletedAllMedia ? 'Close' : 'Cancel'}
          onClose={this.onClose}
          open={drawerOpen}
          ref={this.drawerElement}
          headerIcon={<>
            <LogoBopper style={{ marginRight: baseline }} />
            <Heading is="h4" textColor="bw6">Create your license to activate pricing.</Heading>
          </>}
          actionButton={!this.state.hasDeletedAllMedia && (
            <Button
              label="Save"
              colorScheme="accent"
              size="small-medium"
              variant="pill"
              onClick={this.onClickSave}
              disabled={!this.props.isLicenseValid}
            />
          )}
          position="fullscreen"
          {...classes('', '', className) }
        >{!!steps.length ? steps : (
          <div {...classes('error')}>
            <IconWarningLarge />
            <Heading is="h2">Something went wrong.</Heading>
            <Heading is="h4">Please try again later.</Heading>
          </div>
        )}
        </Drawer>
      </>
    );
  }

  private setCurrentDisplayState(prevProps?: LicenseBuilderProps) {
    let step: LicenseBuildingStepNumber = 1;
    if (this.props.editingUsageIndex !== null) {
      const usage = this.props.licenseUsages[this.props.editingUsageIndex];
      if (usage) {
        step = usage.contextual_param || usage.spot_count ?
          contextualParamStep
          : usage.term ?
            spotCountStep
            : termStep;

        if (programmingMedia.indexOf(usage.medium.id) > -1) {
          step = contextualParamStep;
        }
      }
    }

    const stateUpdate: Partial<IOwnState> = {};

    if (step !== this.state.currentStep) {
      stateUpdate.currentStep = step || (this.state.currentStep + 1 as LicenseBuildingStepNumber);

      if (step > this.state.currentStep && this.state.hasDeletedAllMedia) {
        stateUpdate.hasDeletedAllMedia = false;
      }
    }

    if (prevProps && prevProps.licenseUsages.length > 0 && this.props.licenseUsages.length === 0) {
      stateUpdate.hasDeletedAllMedia = true;
    }

    if (!isEmpty(stateUpdate)) {
      this.setState(stateUpdate as IOwnState);
    }
  }
}

const ConnectedLicenseBuilder = connect<IOwnStateProps, {}, IOwnProps, IStoreState>(
  ({ license, staticData: { licenseComponents } }) => ({
    editingUsageIndex: license.editingUsageIndex,
    isLicenseValid: license.isValid,
    licenseComponents,
    licenseUsages: license.usages,
    termsState: license,
  }),
)(LicenseBuilder);

const LicenseBuilderWithContexts: React.SFC<IOwnProps> = (props) => (
  <OnboardingContext.Consumer>
    {({ unlockAchievement }) => (
      <ConnectedLicenseBuilder
        onboarding={{ unlockAchievement }}
        {...props}
      />
    )}
  </OnboardingContext.Consumer>
);

export default LicenseBuilderWithContexts;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/forms/LicenseBuilder/index.tsx